import type { ReactNode } from "react";

/**
 * The one hero layout for interior pages: white, left-aligned to the logo, text left and a
 * product visual right (or a single readable column when there's no visual).
 * Title scale is fixed here so every page matches: 50px desktop.
 */
export function PageHero({
  crumb,
  title,
  lede,
  actions,
  visual,
  below,
}: {
  crumb?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  visual?: ReactNode;
  below?: ReactNode;
}) {
  const text = (
    <div className="tick-in">
      {crumb ? <div className="text-[14px] text-soft-black">{crumb}</div> : <div className="h-1 w-[72px] bg-vibrant-orange" aria-hidden="true" />}
      <h1 className="mt-6 text-[36px] sm:text-[44px] lg:text-[50px] leading-[1.06] font-bold tracking-tight text-dark-indigo">{title}</h1>
      {lede && <div className="mt-5 max-w-[36em] text-[18px] lg:text-[19px] leading-relaxed text-soft-black">{lede}</div>}
      {actions && <div className="mt-8 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-7">{actions}</div>}
      {below && <div className="mt-8">{below}</div>}
    </div>
  );
  return (
    <section className="bg-white border-b border-softblack-tint-20">
      <div className="container mx-auto px-4 py-14 lg:py-20">
        {visual ? (
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            {text}
            <div className="w-full max-w-[560px] lg:justify-self-end">{visual}</div>
          </div>
        ) : (
          <div className="max-w-3xl">{text}</div>
        )}
      </div>
    </section>
  );
}

/** Secondary hero action: a plain royal text link. */
export function HeroLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 text-[16px] font-bold text-royal-indigo hover:text-dark-indigo">
      {children}
    </button>
  );
}
